import {
  SupportTicketFilterNames,
  supportTicketFilterData,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import {
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { useQuery } from "@tanstack/react-query";
import { useContext, useMemo } from "react";
import { catchApiErrors } from "../db/error-handling";
import { useQueryOptions } from "../filters/filters-context";
import type { StatusValue } from "../filters/support-tickets-filter-toolbar";

const STATUS_VALUES: StatusValue[] = [
  "OPEN",
  "CLOSED",
  "IN_PROGRESS",
  "SNOOZED",
];

function getLeafFilters(group: FilterGroup): AdvancedFilterData[] {
  const filters: AdvancedFilterData[] = [];
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      filters.push(...getLeafFilters(clause));
    } else {
      filters.push(clause);
    }
  }
  return filters;
}

function buildStatusVariant(
  baseFilters: AdvancedFilterData[],
  status: StatusValue,
): FilterGroup {
  const statusFilter: ArrayStringFilterData = {
    ...(supportTicketFilterData[
      SupportTicketFilterNames.STATUS
    ] as ArrayStringFilterData),
    value: [status],
  };
  return {
    operator: FilterOperator.AND,
    clauses: [...baseFilters, statusFilter],
  };
}

export type StatusCounts = Record<StatusValue | "all", number>;

export function useActiveViewStatusCounts(): StatusCounts {
  const { filterGroup } = useQueryOptions();
  const effectApi = useRequiredContext(EffectApiClientContext);
  const team = useContext(TeamContext);

  // Base filters = current filter group minus the status filter
  const baseFilters = useMemo(() => {
    return getLeafFilters(filterGroup).filter(
      (f) => f.name !== SupportTicketFilterNames.STATUS,
    );
  }, [filterGroup]);

  const encodedFilterGroupKey = useMemo(() => {
    try {
      return encodeAdvancedFilterGroup(filterGroup);
    } catch {
      return "";
    }
  }, [filterGroup]);

  const { data } = useQuery<StatusCounts>({
    queryKey: ["activeViewStatusCounts", encodedFilterGroupKey],
    queryFn: async (): Promise<StatusCounts> => {
      const views = STATUS_VALUES.map((status) => ({
        viewKey: status,
        filterGroup: (() => {
          try {
            return encodeAdvancedFilterGroup(
              buildStatusVariant(baseFilters, status),
            );
          } catch {
            return null;
          }
        })(),
      }));

      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        catchApiErrors(
          client.supportTicketCounts.getSupportTicketCounts({
            payload: { views },
          }),
        ),
      );

      const counts = result as Record<string, number>;
      const allCount = STATUS_VALUES.reduce(
        (sum, s) => sum + (counts[s] ?? 0),
        0,
      );

      return {
        OPEN: counts.OPEN ?? 0,
        CLOSED: counts.CLOSED ?? 0,
        IN_PROGRESS: counts.IN_PROGRESS ?? 0,
        SNOOZED: counts.SNOOZED ?? 0,
        all: allCount,
      };
    },
    enabled: !!team,
    staleTime: 0,
  });

  return data ?? { OPEN: 0, CLOSED: 0, IN_PROGRESS: 0, SNOOZED: 0, all: 0 };
}
